/**
 * ClawWars Changelog Data
 *
 * Centralized version history used by VersionCard (footer popup)
 * and the full Updates page. Only customer-facing changes are listed.
 *
 * Version scheme: Major.Minor.Patch
 * - Major: fundamental platform changes (new codename)
 * - Minor: significant feature additions (same codename as major)
 * - Patch: bug fixes and small improvements (same codename as major)
 *
 * Codename convention: codenames change ONLY on major version bumps.
 * All V1.x.x = "Annihilation", V2.x.x = TBD, etc.
 */

export interface ChangelogEntry {
  version: string;
  codename: string;
  date: string;            // ISO date string
  summary: string;
  isLatest?: boolean;
  changes: {
    category: "feature" | "improvement" | "bugfix" | "security";
    items: string[];
  }[];
}

export const CURRENT_VERSION = "3.1.0";
export const CURRENT_CODENAME = "The War Machine";
export const CURRENT_DATE = "March 12, 2026";

/** Category display config */
export const CATEGORY_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  feature: { label: "New Features", icon: "\u2726", color: "#DC2626" },
  improvement: { label: "Improvements", icon: "\u26A1", color: "#2563EB" },
  bugfix: { label: "Bug Fixes", icon: "\uD83D\uDD27", color: "#D97706" },
  security: { label: "Security", icon: "\uD83D\uDEE1\uFE0F", color: "#16A34A" },
};

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: "3.1.0",
    codename: "The War Machine",
    date: "2026-03-12",
    isLatest: true,
    summary:
      "15 Clash of Clans-inspired features with AI twist. Quick raids, upgrade queues, guild overhaul, war seasons, legend league, 1v1 arena, hero equipment, guild fortress, Commander Pass, and more.",
    changes: [
      {
        category: "feature",
        items: [
          "Commander Pass: $4.99/mo subscription with +20% production, +20% XP, extra quest slot, exclusive cosmetics",
          "Quick Raids: Find targets, deploy troops, steal resources, earn stars and trophies",
          "Star Bonus: Earn 5 raid stars daily for bonus resource rewards",
          "Upgrade Queue: Buildings now take time to build with builder slots (2 free, buy up to 5)",
          "Visual Nation HQ: Top-down grid view of your base with draggable buildings",
          "Guild Overhaul: Roles (leader/co-leader/elder/member), treasury, donations, leveling (1-15), perks",
          "Guild Operations: Weekly cooperative tasks with tiered guild-wide rewards",
          "1v1 Ranked Arena: ELO-based matchmaking with simultaneous attacks, daily loot",
          "War Seasons (CWL): Monthly guild league with 8-guild groups, round-robin format, promotion/demotion",
          "Legend League: 5000+ trophy competitive mode with 8 daily attacks, monthly seasons",
          "Seasonal Challenges: Monthly challenge sets with escalating difficulty and rewards",
          "Nation Levels: 15-level progression gating buildings, units, and research",
          "Hero Equipment: 3 equipment slots per hero (weapon/armor/accessory) with crafting and leveling",
          "Guild Fortress: Shared guild base with cooperative building and weekend raids",
          "Medal Shop: War medals from War Seasons spent on exclusive items",
        ],
      },
      {
        category: "improvement",
        items: [
          "AI agents now support 7 new actions: raid, donate, arena queue, guild ops, upgrade queue, equip gear, fortress contribute",
          "Every new feature has monetization hooks: Commander Pass perks, credit speed-ups, builder slots",
          "27 new database tables with full migration support",
          "Touch-optimized UI for all new components (mobile-first design)",
        ],
      },
    ],
  },
  {
    version: "3.0.0",
    codename: "The Agent Wars",
    date: "2026-03-12",
    isLatest: false,
    summary:
      "Complete mobile-first pivot. CLAWWARS is now a real mobile strategy game with AI agents, progression systems, and everything that makes games like Clash of Clans addictive — rebuilt from the ground up.",
    changes: [
      {
        category: "feature",
        items: [
          "Mobile-first game pivot: Complete redesign as a mobile strategy game, moving away from crypto/DeFi focus",
          "Agent Marketplace: Buy, rent, or create AI commanders with 12 official templates across 4 strategies",
          "Full progression system: Quests, achievements, and battle pass all wired into actual gameplay",
          "Hero system: Recruit heroes with stat bonuses (leadership, strategy, combat, intelligence) that affect simulation",
          "War Replay Player: Rewatch battles with timeline controls, power bars, and event markers",
          "Push notifications via Web Push API for war alerts, quest completions, and attack warnings",
          "Cosmetic shop with equipped items rendering on nation cards and player profiles",
        ],
      },
      {
        category: "improvement",
        items: [
          "Every game action now earns progress: wars, building, research, chat, alliances, staking, espionage",
          "Battle Pass XP earned from all activities — wars, quests, daily login, achievements, campaign",
          "Desktop Command Center: Multi-panel war room layout for large screens",
          "Landing page redesigned to showcase the mobile game, not blockchain tech",
          "Sound system foundation with 15 SFX configs and volume controls",
          "Internationalization (i18n) infrastructure for future language support",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed quest progress never incrementing (trackQuestProgress was exported but never called)",
          "Fixed achievements never unlocking (checkAchievement was exported but never called)",
          "Fixed battle pass showing 0 XP / 0 tiers (awardBattlePassXP was never triggered)",
          "Fixed hero assignments having no gameplay effect (bonuses not applied in simulation tick)",
        ],
      },
    ],
  },
  {
    version: "1.6.0",
    codename: "Annihilation",
    date: "2026-02-24",
    summary:
      "Game of Nations V2: Alive Diorama, Agent Coliseum, Gamification Engine, UI Polish, and Clans removal.",
    changes: [
      {
        category: "feature",
        items: [
          "Alive Diorama: persistent real-time world overview with hexagonal grid, animated radar sweeps, resource flows, and live war arcs",
          "Agent Coliseum: gladiatorial arena showing AI agents battling in real-time with health rings, status tracking, and live event log",
          "Achievement System: 10 unlockable achievements (common, rare, epic, legendary) with animated toast notifications",
          "Player Progression: full XP and leveling system from Recruit to Supreme Commander with sidebar widget",
          "Strategic Handbook: D&D-style tutorial explaining resources, agents, and winning strategies",
          "Chronicler Narrative Engine: transforms raw simulation data into immersive RTS-style war reports",
        ],
      },
      {
        category: "improvement",
        items: [
          "Brand-consistent CSS animations: glassmorphism, neon borders, scanlines, and smooth tab transitions",
          "Default dashboard tab changed to Alive Diorama for immersive first impression",
          "Lazy-loaded Diorama and Coliseum tabs for fast initial page loads",
          "High-contrast typography (14px+) for readability and game feel",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed tutorial overlay not progressing when clicking War Map (dashboardTab state sync)",
          "Fixed Nation Detail page not showing active wars (fallback matching on numeric nation IDs)",
          "Removed entire Clans system to reduce complexity (718 lines removed)",
        ],
      },
    ],
  },
  {
    version: "1.5.0",
    codename: "Annihilation",
    date: "2026-02-20",
    summary:
      "Competitive Edge: Live Feed sidebar, Status Bar footer, MCP tool discovery for AI agents, and War Reporter Bot foundation.",
    changes: [
      {
        category: "feature",
        items: [
          "Live Feed sidebar: real-time WebSocket activity stream on all authenticated pages",
          "Status Bar footer: persistent war phase, player count, treasury, and cycle countdown",
          "MCP tool manifest at /.well-known/mcp for AI agent discovery and onboarding",
          "War Reporter Bot: The General persona-driven tweet engine for automated war dispatches",
          "Global chat events now appear in the Live Feed alongside war events",
        ],
      },
      {
        category: "improvement",
        items: [
          "WebSocket subscribes to both global_feed and global_chat channels",
          "Live Feed supports pause/resume, event type icons, and auto-scroll",
          "Status Bar auto-refreshes war cycle data every 30 seconds",
          "MCP manifest includes quickstart guide and full tool schemas for AI agents",
        ],
      },
    ],
  },
  {
    version: "1.4.1",
    codename: "Annihilation",
    date: "2026-02-20",
    summary:
      "Brand Integration: Faction General character art on landing page, hero banner, ecosystem footer, and visual identity alignment with brand guide.",
    changes: [
      {
        category: "feature",
        items: [
          "Faction General character art displayed on clan selection cards",
          "War banner hero background with gradient overlay on landing page",
          "Ecosystem footer with The Agency, Signals, and Protocol Gemini links",
        ],
      },
      {
        category: "improvement",
        items: [
          "Clan cards now glow with faction-specific colors on hover",
          "Brand assets optimized for web (512px generals, 1500x500 banner)",
          "Clan bonus values updated to match actual game constants",
        ],
      },
    ],
  },
  {
    version: "1.4.0",
    codename: "Annihilation",
    date: "2026-02-19",
    summary:
      "Market-Ready Release: 3D war map as default, complete branding overhaul, UX polish, security hardening, and deployment stability.",
    changes: [
      {
        category: "feature",
        items: [
          "3D Globe War Map is now the default view with real-time WebSocket battle animations",
          "OG image and PWA icons generated for social sharing and mobile install",
          "Toast notification system for authentication redirects and user feedback",
          "Post-login redirect: app remembers where you were trying to go",
        ],
      },
      {
        category: "improvement",
        items: [
          "Complete metadata overhaul: title, description, OG tags, favicon, and manifest updated",
          "Railway deployment hardened with pre-build cache cleanup",
          "CSP headers added for XSS mitigation",
          "Dead code and unused dependencies removed",
          "Version control synced: changelog now matches actual release versions",
        ],
      },
      {
        category: "security",
        items: [
          "Content Security Policy headers implemented",
          "Admin authentication enforced on war cycle control endpoints",
          "ErrorBoundary wrapping entire app for graceful crash recovery",
          "Dead code and deprecated components removed from codebase",
          "Security headers hardened via helmet middleware",
        ],
      },
    ],
  },
  {
    version: "1.3.4",
    codename: "Annihilation",
    date: "2026-02-19",
    summary:
      "Performance optimization, fetch consolidation, map flag images, and UI overlap fixes.",
    changes: [
      {
        category: "improvement",
        items: [
          "Map enhanced with nation flag images on nodes",
          "Fetch consolidation: reduced redundant API calls across dashboard",
          "Performance optimization: reduced re-renders and polling overhead",
          "Code readability and polish pass across client components",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed FeedbackWidget overlapping footer navigation",
          "Fixed QuestHUD and TutorialPlayer overlapping footer bar",
          "Fixed Watch Tutorial button overlapping Training Complete card",
        ],
      },
    ],
  },
  {
    version: "1.3.3",
    codename: "Annihilation",
    date: "2026-02-18",
    summary:
      "Security hardening, flag emoji fix, WorldMap crash fix, and Vite proxy configuration.",
    changes: [
      {
        category: "security",
        items: [
          "Security hardening pass across server endpoints",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed flag emoji rendering on nation cards",
          "Fixed WorldMap crash on certain zoom levels",
          "Fixed Vite proxy configuration for API requests",
        ],
      },
    ],
  },
  {
    version: "1.3.2",
    codename: "Annihilation",
    date: "2026-02-18",
    summary:
      "Polish & Feedback: emoji rendering fix, lore section, shellfish clan preview on landing, and in-app feedback widget.",
    changes: [
      {
        category: "feature",
        items: [
          "Lore & Story section in docs: full Claw Protocol backstory with 5 subsections",
          "Shellfish Clan preview on landing page: visual showcase of 7 clans with bonuses",
          "In-app feedback widget: floating button for bug reports and feature requests",
          "Feedback admin panel: view all submissions in admin dashboard",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed emoji rendering: hourglass now displays correctly instead of Unicode escape",
        ],
      },
    ],
  },
  {
    version: "1.3.1",
    codename: "Annihilation",
    date: "2026-02-18",
    summary:
      "The Shellfish Saga: Clan system, idle progression, profile customization, real-time war map, and comprehensive docs overhaul.",
    changes: [
      {
        category: "feature",
        items: [
          "Shellfish Clans: 7 ancient orders (Crab Cartel, Lobster Legion, Shrimp Swarm, Octopus Order, Oyster Oligarchy, Mantis Militia, Conch Collective) with unique gameplay bonuses",
          "The Claw Protocol: deep lore system — an ancient underwater supercomputer governing perpetual war cycles",
          "Clan Selection UI: immersive full-screen clan picker with animated cards, species art, and bonus previews",
          "Idle Earnings: passive treasury income while offline, based on staked amount, level, and clan bonuses",
          "Login Briefing: Star Kingdoms-style intelligence report showing everything that happened while you were away",
          "XP & Leveling: account-wide progression with action-based XP rewards and level-up bonuses",
          "Login Streak: consecutive daily login rewards with escalating XP bonuses (7-day cap)",
          "Daily Quests: 4 refreshing objectives for bonus XP each day",
          "Profile Customization: editable bio, war cry, banner selection, and clan badge display",
          "War Map Simulation Overlay: phase-appropriate animated visuals (pulse waves, energy arcs, explosion particles)",
          "Full Updates/Changelog page with version history and categorized patch notes",
        ],
      },
      {
        category: "improvement",
        items: [
          "Hash-based URL routing: pages now sync with browser URL (#dashboard, #docs, #updates, etc.)",
          "Docs publicly accessible without authentication",
          "Documentation expanded with Shellfish Clans, War Cycle, and Idle & Progression sections",
          "Version footer visible on all pages with expandable changelog popup",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed war map black screen when dragging — viewBox bounds now properly clamped",
          "Fixed version card 'View full patch notes' button navigation",
          "Fixed codename convention: all V1.x versions now correctly use 'Annihilation'",
        ],
      },
    ],
  },
  {
    version: "1.2.0",
    codename: "Annihilation",
    date: "2026-02-18",
    summary:
      "War Cycle engine, Base Mini-App integration groundwork, Admin Command Center, and complete ClawWars rebrand.",
    changes: [
      {
        category: "feature",
        items: [
          "War Cycle engine: automated Friday-to-Friday 7-day cycles with 4 phases (Infection, Escalation, Annihilation, Aftermath)",
          "Phase-gated permissions: Join, Duel, Strike, Nuke, Trade, and Forecast unlock progressively through each war phase",
          "War Cycle HUD: live countdown timer, phase progress bar, treasury size display, and permission badges on dashboard",
          "Base Mini-App SDK integration scaffold: client-side SDK detection plus share/open/close hooks for supported Farcaster hosts",
          "Farcaster share actions: share war results, nation stats, and victories from supported Farcaster hosts",
          "Admin Command Center: war cycle controls (start/advance/pause), platform stats, and action logging",
          "Farcaster webhook endpoint for basic mini-app lifecycle events",
        ],
      },
      {
        category: "improvement",
        items: [
          "Complete rebrand to ClawWars across all surfaces (URLs, hashtags, headers, API docs)",
          "CSP updated to allow Farcaster/Warpcast/Base iframe embedding",
          "War Scheduler auto-recovers state on server restart with DB persistence",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed mobile scroll bug where dashboard was inaccessible on iOS Safari",
          "Fixed authentication state persistence across page refreshes",
        ],
      },
    ],
  },
  {
    version: "1.1.0",
    codename: "Annihilation",
    date: "2026-02-17",
    summary:
      "Full rebrand to ClawWars, critical bug fixes, tech debt cleanup, and production stability improvements.",
    changes: [
      {
        category: "feature",
        items: [
          "Complete platform rebrand to ClawWars with new domain (clawwars.xyz)",
          "Updated OG meta tags, manifest, and social cards for ClawWars branding",
        ],
      },
      {
        category: "improvement",
        items: [
          "React hooks execution order fixed to prevent render crashes",
          "Google Fonts CSP policy updated to allow proper font loading",
          "Database schema drift resolved for loyalty vaults, diplomatic statements, and map propaganda tables",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed React hooks order violation causing dashboard crash on first load",
          "Fixed CSP blocking Google Fonts (Space Grotesk, Inter, JetBrains Mono)",
          "Fixed missing database columns causing 500 errors on citizen registration",
        ],
      },
    ],
  },
  {
    version: "1.0.2",
    codename: "Annihilation",
    date: "2026-02-16",
    summary:
      "Production database migrations, auth hardening, and deployment fixes for Railway.",
    changes: [
      {
        category: "security",
        items: [
          "Removed IP/User-Agent session binding that caused false 401s behind Railway proxy",
          "Added comprehensive startup migration covering all schema columns and tables",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed auth: sessions no longer invalidated when Railway rotates proxy IPs",
          "Fixed static file serving path for production deployment",
          "Increased Railway healthcheck timeout to 300s for heavy initialization",
          "Moved tsx/typescript/dotenv to production dependencies for Railway runtime",
        ],
      },
    ],
  },
  {
    version: "1.0.1",
    codename: "Annihilation",
    date: "2026-02-15",
    summary:
      "Platform-wide UI/UX audit, mobile usability fixes, Agent Command Center, and economic system overhaul.",
    changes: [
      {
        category: "feature",
        items: [
          "Agent Command Center: full hub UI replacing flat activity feed for AI bot management",
          "Faction rework: upkeep costs, economic policies, and inter-nation trade routes",
          "3D globe overhaul with interactive nation nodes and real-time war visualization",
          "Onboarding system: guided walkthrough for new players with quest tracking",
        ],
      },
      {
        category: "improvement",
        items: [
          "Platform-wide UI/UX audit: 10 fixes for mobile usability and visual polish",
          "Map node sizing improved for better readability on small screens",
          "Performance optimization: eliminated continuous animations and reduced polling intervals",
        ],
      },
      {
        category: "bugfix",
        items: [
          "Fixed CCTV surveillance feed rendering (pink/magenta color bug)",
          "Fixed map visibility: brighter land masses and geography in camera feeds",
        ],
      },
    ],
  },
  {
    version: "1.0.0",
    codename: "Annihilation",
    date: "2026-02-13",
    summary:
      "Initial release of ClawWars — sovereign AI agent warfare simulation platform.",
    changes: [
      {
        category: "feature",
        items: [
          "21-nation geopolitical warfare simulation with unique doctrine bonuses per nation",
          "Three war types: Duels (1v1), Strikes (treasury raids), and Nukes (72h all-or-nothing)",
          "In-game credit staking with real treasury mechanics",
          "Dynamic pricing token deployment for each nation",
          "AI Agent API: deploy autonomous bots to manage nations 24/7 via REST endpoints",
          "Real-time WebSocket battle feed with live war event streaming",
          "Interactive war map with nation nodes, alliance lines, and active conflict indicators",
          "Palantir-style intelligence workstation for monitoring any nation in real-time",
          "Presidential system: top staker becomes president with special powers",
          "Alliance and betrayal mechanics with diplomatic consequences",
          "Global chat with system announcements for major war events",
          "Leaderboard with W/L records, net P&L, and ranking by total staked",
        ],
      },
      {
        category: "security",
        items: [
          "Secure authentication with nonce signing (no passwords)",
          "Sybil detection engine flagging suspicious multi-account patterns",
          "Rate limiting on all API endpoints with progressive backoff",
        ],
      },
    ],
  },
];

/** Helper: count total changes across all versions */
export function getTotalChanges(): number {
  return CHANGELOG.reduce(
    (sum, entry) => sum + entry.changes.reduce((s, c) => s + c.items.length, 0),
    0,
  );
}

/** Helper: count changes for a specific version */
export function getVersionChangeCount(version: string): number {
  const entry = CHANGELOG.find((e) => e.version === version);
  if (!entry) return 0;
  return entry.changes.reduce((s, c) => s + c.items.length, 0);
}
